<template>
  <div>
    <div class="tile is-parent">
      <article class="tile is-child box detail-page-tile">
        <label class="label">Quote No.</label>
        <div class="field has-addons">
          <div class="control is-expanded">
            <input class="input uppercase"
              v-model="quoteNo"
              @input="searchQuote"
              placeholder="Start typing to search..."
              v-focus-inserted
              :disabled="isUploading">
            <span v-if="validQuote"
              class="help is-success">Valid Quote No.</span>
            <span v-else-if="quoteNo.length > 0"
              class="help is-danger">Invalid Quote No.</span>
          </div>
        </div>
        <div class="buttons is-right">
          <a class="button is-primary"
            @click="dropzoneRemoveAllFiles"
            :disabled="isUploading || !validQuote">
            <span class="icon">
              <i class="mdi mdi-close mdi-18px" />
            </span>
            <span>Clear</span>
          </a>
          <a class="button is-success"
            @click="upload"
            :disabled="isUploading || !validQuote">
            <span class="icon">
              <i class="mdi mdi-upload mdi-18px" />
            </span>
            <span>Upload</span>
          </a>
        </div>
        <div class="field"
          v-show="isDropzoneActive && isMobile()">
          <label class="label">Select Image Source</label>
          <div class="control">
            <div class="select">
              <select v-model="dropzoneOptions.capture">
                <option :value="mobileImageSourceType.Gallery">From Gallery</option>
                <option :value="mobileImageSourceType.Camera">From Camera</option>
              </select>
            </div>
          </div>
        </div>
        <div class="field"
          v-show="isDropzoneActive">
          <label class="label">Select Image Category</label>
          <div class="select">
            <select v-model="selectedCategoryId"
              @change="clearUploadedFile">
              <option v-for="(cat) in imageCategories"
                :key="cat.quoteImageCategoryId"
                :value="cat.quoteImageCategoryId">
                {{ cat.name }}
              </option>
            </select>
          </div>
        </div>
        <vue-dropzone v-if="validQuote && isDropzoneActive"
          ref="quoteImageDropzone"
          id="dropzone"
          :options="dropzoneOptions"
          @vdropzone-mounted="dropzoneMounted"
          @vdropzone-file-added="dropzoneFileAdded"
          @vdropzone-success="dropzoneSuccess"
          @vdropzone-success-multiple="dropzoneSuccessMultiple"
          @vdropzone-error-multiple="dropzoneErrorMultiple"
          @vdropzone-error="dropzoneError"
          @vdropzone-sending="dropzoneSending"
          @vdropzone-total-upload-progress="dropzoneTotalUploadProgress"
          @vdropzone-complete-multiple="dropzoneCompleteMultiple"
          @vdropzone-queue-complete="dropzoneQueueComplete" />
      </article>
    </div>
  </div>
</template>

<script>
import mimeTypes from 'mime-types'
import VueDropzone from '@/components/VueDropzone'
import { FocusInserted } from '@/components/directives'
import ImageUploaderService from './ImageUploaderService'
import { guidValidator } from '@/components/validators'
import Guid from '@/components/Guid'
import _debounce from 'lodash.debounce'
import { QuoteService } from '@/services'
import { MobileImageSourceType } from '@/enums'
import platform from 'platform'

export default {
  name: 'ImageUploader',
  directives: {
    FocusInserted
  },
  components: {
    VueDropzone
  },
  props: {},
  data() {
    return {
      quoteNo: '',
      quoteId: '',
      isDropzoneActive: false,
      isClearAllActive: false,
      isUploading: false,
      queuedFileCount: 0,
      successFileCount: 0,
      failedFileCount: 0,
      dropzoneOptions: {
        url: `${process.env.VUE_APP_WEBAPI_URI}/quotes/images/upload/${this.quoteId}/${this.mainQuoteNo}/${this.subQuoteNo}/${this.selectedCategoryId}`,
        dictDefaultMessage: 'Drop files here or click to upload',
        autoProcessQueue: false,
        thumbnailWidth: 200,
        thumbnailHeight: 200,
        maxFilesize: 10,
        resizeWidth: 1920,
        resizeHeight: 1920,
        // acceptedFiles: 'image/*',
        acceptedFiles: '.jpg,.jpeg,.png',
        capture: 'image/',
        headers: {
          Authorization: ''
        },
        accept: function (file, done) {
          const mimeType = file.type
          const allowedTypes = [
            mimeTypes.contentType('jpeg'),
            mimeTypes.contentType('jpg'),
            mimeTypes.contentType('png')
          ]
          if (!allowedTypes.includes(file.type)) {
            done()
            return
          }
          if (mimeType !== mimeTypes.contentType('jpeg')) {
            this.options.resizeMimeType = mimeTypes.contentType('jpeg')
            this.options.resizeQuality = 0.95
            done()
            return
          }
          done()
        },
        renameFile(file) {
          const extension = file.name.split('.').pop()
          if (extension !== 'jpg' || extension !== 'jpeg') {
            return `${file.name.substr(0, file.name.lastIndexOf('.'))}.${mimeTypes.extension(
              mimeTypes.contentType('jpeg')
            )}`
            // return file.name.substr(0, file.name.lastIndexOf('.')) + '.jpg'
          } else {
            return file.name
          }
        }
      },
      imageCategories: null,
      selectedCategoryId: ''
    }
  },
  validations: {
    quoteId: {
      validGuid: guidValidator
    }
  },
  computed: {
    validQuote() {
      return this.$v.quoteId.$dirty && !this.$v.quoteId.$error
    },
    mainQuoteNo() {
      const quoteNoArray = this.quoteNo.split('/')
      return quoteNoArray[0].toUpperCase()
    },
    subQuoteNo() {
      const quoteNoArray = this.quoteNo.split('/')
      return quoteNoArray.length > 1 ? quoteNoArray[1] : '0'
    },
    mobileImageSourceType() {
      return MobileImageSourceType
    }
  },
  watch: {
    'dropzoneOptions.capture': function (newVal, oldVal) {
      // Reload vue-dropzone component here
      if (this.isDropzoneActive === true) {
        this.isDropzoneActive = false
        this.$nextTick(() => {
          this.isDropzoneActive = this.validQuote
        })
      }
    }
  },
  created() {
    this.dropzoneOptions.capture = this.isMobile
      ? this.mobileImageSourceType.Gallery
      : this.mobileImageSourceType.Camera
    document.title = this.$route.meta.title
    this.getImageCategories()
  },
  mounted() {},
  methods: {
    searchQuote: _debounce(async function () {
      this.$v.quoteId.$touch()
      if (this.quoteNo) {
        this.quoteId = await ImageUploaderService.getQuoteIdByNo(this.quoteNo)
        this.isDropzoneActive = this.validQuote
        this.dropzoneOptions.url = `${process.env.VUE_APP_WEBAPI_URI}/quotes/images/upload/${this.quoteId}/${this.mainQuoteNo}/${this.subQuoteNo}/${this.selectedCategoryId}`
      } else {
        this.quoteId = Guid.empty()
        this.isDropzoneActive = false
      }
    }, 300),
    upload() {
      if (this.$refs.quoteImageDropzone.getAcceptedFiles().length > 0) {
        const jwtToken = this.$cookie.get(this.$cookie.tokenCookieName)
        // console.log(jwtToken)
        this.dropzoneOptions.headers.Authorization = `Bearer ${jwtToken}`
        this.queuedFileCount = this.$refs.quoteImageDropzone.getQueuedFiles().length
        this.$refs.quoteImageDropzone.processQueue()
      }
    },
    dropzoneMounted() {
      // console.log(this.$refs.quoteImageDropzone.getAcceptedFiles())
    },
    dropzoneFileAdded() {
      this.$refs.quoteImageDropzone.changeUrl(
        `${process.env.VUE_APP_WEBAPI_URI}/quotes/images/upload/${this.quoteId}/${this.mainQuoteNo}/${this.subQuoteNo}/${this.selectedCategoryId}`
      )
      this.isClearAllActive = true
      // this.isUploading = true
    },
    dropzoneSuccessMultiple(files, response) {
      console.log(files)
      this.$toast.open({
        message: `${files.length} images uploaded`,
        type: 'is-success',
        position: 'is-top',
        queue: false,
        duration: 5000
      })
      this.isUploading = false
    },
    dropzoneSuccess(file, response) {
      this.successFileCount++
      // console.log(file)
      // this.images = []
      // this.$toast.open({
      //   message: `${this.queuedFileCount} images uploaded`,
      //   type: 'is-success',
      //   position: 'is-top',
      //   queue: false,
      //   duration: 5000
      // })
      // this.isUploading = false
    },
    dropzoneErrorMultiple(files, message, xhr) {
      this.$toast.open({
        message: `${message}: ${xhr.statusText}`,
        type: 'is-danger',
        position: 'is-top',
        queue: false,
        duration: 5000
      })
      this.isUploading = false
    },
    dropzoneError(files, message, xhr) {
      this.failedFileCount++
      const statusText = xhr ? xhr.statusText : ''
      this.$toast.open({
        message: `${message}: ${statusText}`,
        type: 'is-danger',
        position: 'is-top',
        queue: false,
        duration: 5000
      })
      this.isUploading = false
    },
    dropzoneSending(file, xhr, formData) {
      // console.log(file)
      // for (var value of formData.values()) {
      //   console.log(value)
      // }
    },
    dropzoneCompleteMultiple(files) {
      // console.log(files.count)
    },
    dropzoneQueueComplete(files, xhr, formData) {
      if (this.successFileCount > 0)
        this.$toast.open({
          message: `${this.successFileCount} images uploaded`,
          type: 'is-success',
          position: 'is-top',
          queue: false,
          duration: 5000
        })
      this.isUploading = false
      this.queuedFileCount = 0
      this.successFileCount = 0
      this.failedFileCount = 0
    },
    dropzoneTotalUploadProgress(totaluploadprogress, totalBytes, totalBytesSent) {
      this.isUploading = totaluploadprogress < 100
      // console.log(totaluploadprogress)
    },
    dropzoneRemoveAllFiles() {
      if (this.isUploading || !this.validQuote) {
        return
      }

      this.$refs.quoteImageDropzone.removeAllFiles()
      this.isClearAllActive = false
    },
    isMobile() {
      return platform.os.family.toLowerCase() === 'android' || platform.os.family.toLowerCase() === 'ios'
    },
    async getImageCategories() {
      this.imageCategories = await QuoteService.getQuoteImageCategories()
      this.selectedCategoryId = this.imageCategories[0].quoteImageCategoryId
    },
    clearUploadedFile() {
      this.$refs.quoteImageDropzone.removeAllFiles()
    }
  }
}
</script>

<style lang="scss">
@import '../../assets/style/mixins/inputs/placeholder';

input.uppercase {
  text-transform: uppercase;
  @include reset-placeholder-text-transform;
}
</style>
